import { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AdminContent } from './components';

import { Loader } from '@/components';
import { Layout } from '@/components/GenericsTournaments';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import { getFullInitMl, getListPendingMl } from '@/services/minileagues';
import { Maybe } from '@/types';
import {
  MiniLigasTO,
  ResponseInitMiniligas,
  ResponseListParticipatingML,
} from '@/types/minileagues';
import { TEXTS } from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

interface State {
  init: Maybe<ResponseInitMiniligas>;
  recommendedList: MiniLigasTO | null;
  playingList: ResponseListParticipatingML | null;
  previousTourneys: MiniLigasTO | null;
  invitationList: MiniLigasTO | null;
  mounted: boolean;
}

const initialState: State = {
  init: null,
  recommendedList: null,
  playingList: null,
  previousTourneys: null,
  mounted: false,
  invitationList: null,
};

const AdminMinileague = () => {
  const toast = useToast();
  const [state, setState] = useState<State>(initialState);
  const { tourneyData, updateTourney } = useTourney();
  const navigate = useNavigate();

  const { data, isLoading, isRefetching } = useQuery(
    'getFullInitMl',
    async () => await getFullInitMl(),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    }
  );

  useEffect(() => {
    if (
      !isLoading &&
      data?.init?.estado &&
      data?.listPendingMl.estado &&
      data?.listParticipatingMl.estado &&
      data?.listPreviousMl.estado &&
      data?.listSuggestedUsersMl.estado &&
      !isRefetching
    ) {
      const {
        init,
        listPendingMl,
        listParticipatingMl,
        listPreviousMl,
        listSuggestedUsersMl,
      } = data;

      setState((prevState) => ({
        ...prevState,
        init,
        recommendedList: listSuggestedUsersMl.miniLigasSugeridas || null,
        previousTourneys: listPreviousMl.miniLigasAnt || null,
        playingList: listParticipatingMl || null,
        pendingList: listPendingMl.misMiniLigasPostuladas || null,
        invitationList: listPendingMl.misMiniLigas || null,
        mounted: true,
      }));

      updateTourney({
        tourneyStarted: init?.comenzaron,
        tourneysPlaying: listParticipatingMl?.misMiniLigas || [],
        tourneysPending: listPendingMl.misMiniLigasPostuladas || [],
        maxNumParticipants: init?.cantPartMaxXML,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isRefetching]);

  // clean state
  useEffect(() => {
    return () => {
      setState(initialState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refresco pendientes de aceptacion al enviar postulacion
  const onHandleSubmit = useCallback(async () => {
    const refreshData = await getListPendingMl();
    if (refreshData?.estado) {
      setState((prevState) => ({
        ...prevState,
        pendingList: refreshData.misMiniLigasPostuladas || null,
      }));
    } else {
      toast.show(TEXTS.defaultError);
    }
  }, [toast]);

  // effecto escucha al enviar postulacion
  useEffect(() => {
    if (tourneyData.idsPendings.length > 0 && state.mounted) {
      onHandleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.idsPendings]);

  return (
    <>
      <Loader isVisible={!state.mounted} />
      <Layout
        adsSections={SECTION_NAME_FOR_ADS.miniligas}
        className="px-0 md:px-4 pt-4 lg:pt-0"
        main={() => (
          <div className=" lg:min-h-[600px]">
            <AdminContent />
          </div>
        )}
        mobile={{
          leftContent: {
            backAction: () => {
              navigate(ROUTES.minileagues.home);
            },
          },
          centerContent: { title: 'Administrar Miniliga' },
        }}
      />
    </>
  );
};

export { AdminMinileague };
